import * as Yup from "yup";
import { Stack } from "@mui/material";
import { useFormikContext } from "formik";
import PaymentMethod from "./PaymentMethod";
import { useContext } from "react";
import { Bank, Cash, Order, PaymentMethodsType } from "@/types";
import { WebsiteContext } from "@/pages/website/WebsiteProvider";
import { BankIcon, Cash02Icon, CreditCardPosIcon } from "@hugeicons/core-free-icons";
import { bankOptions, creditCardOptions, currencyOptions } from "@/constants/paymentMethods";
import CreditCardDetails from "./CreditCardDetails";
import BankTransferOptions from "./BankTransferOptions";
import CashDetails from "./CashDetails";
import { useAppSelector } from "@/hooks";
import { RootState } from "@/store/store";
import { getOrderTotal } from "@/utils/order";
import useServiceFee from "@/hooks/useServiceFee";

export type IPaymentForm = {
  paymentDetails: Order["paymentDetails"];
};

export const paymentFormInitialValues = {
  paymentDetails: null,
};

export const PaymentFormSchema = Yup.object().shape({
  paymentDetails: Yup.object().required(),
});

const PaymentForm = () => {
  const formik = useFormikContext<IPaymentForm>();
  const { organization } = useContext(WebsiteContext);
  const settings = organization?.settings;
  const order = useAppSelector((state: RootState) => state.order);
  const service = useServiceFee();

  const totalAmount = getOrderTotal(order.items, organization?.settings?.includeTax, service.fee);

  const groupedBankAccounts = settings?.paymentMethods?.bankTransfer?.reduce(
    (acc, current) => {
      return {
        ...acc,
        [current.bankName]: [...(acc[current.bankName] || []), { accountNumber: current.accountNumber, currency: current.currency }],
      };
    },
    {} as Record<string, { accountNumber: string; currency: string }[]>,
  );

  return (
    <Stack spacing={2}>
      {settings?.paymentMethods?.cash ? (
        <>
          <PaymentMethod label="Efectivo" icon={Cash02Icon} isChecked={formik.values.paymentDetails?.method === PaymentMethodsType.CASH}>
            <CashDetails
              total={totalAmount}
              amount={(formik.values.paymentDetails?.value as Cash)?.amount ?? ""}
              onAmountChange={(amount) =>
                formik.setFieldValue("paymentDetails", {
                  method: PaymentMethodsType.CASH,
                  value: { amount },
                })
              }
            />
          </PaymentMethod>
        </>
      ) : null}
      {settings?.paymentMethods?.bankTransfer ? (
        <PaymentMethod icon={BankIcon} label="Transferencia Bancaria" isChecked={formik.values.paymentDetails?.method === PaymentMethodsType.BANK_TRANSFER}>
          <Stack py={2}>
            {groupedBankAccounts &&
              Object.entries(groupedBankAccounts).map(([bankId, accounts]) =>
                accounts.map((account) => (
                  <BankTransferOptions
                    key={account.accountNumber}
                    bankName={bankOptions.find((bank) => bank.value === bankId)?.label || bankId}
                    accountNumber={account.accountNumber}
                    currency={currencyOptions.find((cur) => cur.value === account.currency)?.label || account.currency}
                    isChecked={(formik.values.paymentDetails?.value as Bank)?.accountNumber === account.accountNumber}
                    onChange={() =>
                      formik.setFieldValue("paymentDetails", {
                        method: PaymentMethodsType.BANK_TRANSFER,
                        value: {
                          bankName: bankOptions.find((bank) => bank.value === bankId)?.label || bankId,
                          accountNumber: groupedBankAccounts[bankId][0].accountNumber,
                          currency: groupedBankAccounts[bankId][0].currency,
                        },
                      })
                    }
                  />
                )),
              )}
          </Stack>
        </PaymentMethod>
      ) : null}
      {settings?.paymentMethods?.stripeEnabled ? (
        <PaymentMethod
          icon={CreditCardPosIcon}
          label="Tarjeta de Crédito / Débito"
          isChecked={formik.values?.paymentDetails?.method === PaymentMethodsType.CREDIT_CARD}
          onChange={() => formik.setFieldValue("paymentDetails", { method: PaymentMethodsType.CREDIT_CARD, value: null })}
        />
      ) : settings?.paymentMethods?.creditCard?.length ? (
        <PaymentMethod icon={CreditCardPosIcon} label="Tarjeta de Crédito" isChecked={formik.values?.paymentDetails?.method === PaymentMethodsType.CREDIT_CARD}>
          <Stack py={2}>
            {settings.paymentMethods.creditCard.map((cardId, index) => (
              <CreditCardDetails
                key={index}
                isChecked={formik.values.paymentDetails?.value === cardId}
                label={creditCardOptions.find((card) => card.value === cardId)?.label || cardId}
                onChange={() => formik.setFieldValue("paymentDetails", { method: PaymentMethodsType.CREDIT_CARD, value: cardId })}
              />
            ))}
          </Stack>
        </PaymentMethod>
      ) : null}
    </Stack>
  );
};

export default PaymentForm;
